import { isEmptyString } from "../validations";
import type { CustomizationHandler } from "./types";

/**
 * The price on the product page (PDP) only. Product grids get a target of
 * their own: there every card has a different price, so they cannot share
 * this one's "same value on every match" semantics.
 *
 * `.js-price-display` alone also matches grid cards, quick shop and sticky
 * bars. Scoping it to `.js-price-container`, which only the product page
 * renders, leaves the one price the shopper is looking at: in all 24 Core
 * themes with NubeSDK slots, and always as a plain-text element, so
 * `textContent` loses no markup.
 *
 * The theme rewrites this text on every variant change
 * (`.js-price-display.text(variant.price_short)`), hence the app re-applying
 * it on `product:variant_selected`.
 *
 * @see ./README.md
 */
export const productDetailPrice: CustomizationHandler<"product-detail-price"> =
	{
		host: "storefront",
		selector: ".js-price-container .js-price-display",

		read(element) {
			return { text: element.textContent ?? "" };
		},

		operation(element, options) {
			// `textContent` only: the theme keeps the numeric price in the
			// `content` / `data-product-price` attributes, which its discount
			// and installment maths read and which this leaves untouched.
			if (options.text !== undefined) element.textContent = options.text;
		},

		values(element) {
			return {
				// What the shopper reads, trimmed like the button's label.
				text: (element.textContent ?? "").trim(),
			};
		},

		validate(_element, options) {
			if (options.text !== undefined && isEmptyString(options.text)) {
				return 'refused "text": a price cannot be blank';
			}
			return null;
		},
	};
